import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';
import type { ApiResponse } from '@fluenceur/types';

// Types matching backend ShopifyProductService
export interface ShopifyVariant {
  id: string;
  title: string;
  sku: string | null;
  priceInCents: number;
  available: boolean;
  inventoryQuantity: number | null;
  options: { name: string; value: string }[];
}

export interface ShopifyProduct {
  id: string;
  title: string;
  description: string | null;
  handle: string;
  status: 'ACTIVE' | 'ARCHIVED' | 'DRAFT' | 'UNLISTED';
  featuredImageUrl: string | null;
  images?: { url: string; altText: string | null }[];
  variants: ShopifyVariant[];
  totalInventory: number;
  priceRange: {
    min: { amount: string; currencyCode: string };
    max: { amount: string; currencyCode: string };
  };
}

export interface ProductListResult {
  products: ShopifyProduct[];
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
}

interface UseShopifyProductsQueryOptions {
  search?: string;
  enabled?: boolean;
  limit?: number;
}

export const shopifyProductsQueryKey = (search?: string) =>
  ['shopify-products', search ?? ''] as const;

export const useShopifyProductsQuery = ({
  search,
  enabled = true,
  limit = 20,
}: UseShopifyProductsQueryOptions) => {
  return useInfiniteQuery({
    queryKey: shopifyProductsQueryKey(search),
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (pageParam) params.set('cursor', pageParam);
      params.set('limit', String(limit));

      const response = await api.get<ApiResponse<ProductListResult>>(
        `/dashboard/shopify/products?${params.toString()}`
      );
      return response.data.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage?.pageInfo?.hasNextPage ? lastPage.pageInfo.endCursor : undefined,
    enabled,
  });
};

interface UseShopifyProductQueryOptions {
  productId: string;
  enabled?: boolean;
}

export const shopifyProductQueryKey = (productId: string) =>
  ['shopify-product', productId] as const;

export const fetchShopifyProduct = async (productId: string): Promise<ShopifyProduct> => {
  const response = await api.get<ApiResponse<ShopifyProduct>>(
    `/dashboard/shopify/products/${productId}`
  );
  if (!response.data.data) throw new Error('Invalid Shopify product response');
  return response.data.data;
};

export const shopifyProductQueryOptions = (productId: string) => ({
  queryKey: shopifyProductQueryKey(productId),
  queryFn: () => fetchShopifyProduct(productId),
});

export const useShopifyProductQuery = ({
  productId,
  enabled = true,
}: UseShopifyProductQueryOptions) => {
  return useQuery({
    ...shopifyProductQueryOptions(productId),
    enabled: enabled && !!productId,
  });
};
